<template>
  <div
    class="flsa-timeline-item d-flex align-center ga-2 px-8 w-100"
    :class="{
      'flsa-timeline-item-interactive': !isSelected,
      'flsa-timeline-item-read-only': isSelected,
      'flsa-timeline-item-first': isFirst,
      'flsa-timeline-item-last': isLast,
    }"
    @click="handleClick"
  >
    <div
      v-if="showConnector"
      class="flsa-timeline-item-connector flex-shrink-0 position-relative align-self-stretch"
    >
      <div class="flsa-timeline-item-line position-absolute" />
      <div
        class="flsa-timeline-item-dot position-absolute rounded-circle"
        :class="{ 'flsa-timeline-item-dot-selected': isSelected }"
      />
    </div>

    <div class="flsa-timeline-item-content d-flex flex-column ga-1 py-4 flex-grow-1 min-w-0">
      <StatusPill
        v-if="showJurisdictionPill"
        :status-key="jurisdictionStatusKey(submission.jurisdiction)"
        :label="jurisdictionLabel(submission.jurisdiction)"
      />

      <div class="d-flex align-center ga-2 flex-wrap">
        <span class="text-h2 text-textDarkest">
          {{ formatFlsaSubmissionDate(submission.created_at) }}
        </span>
        <CurrentVersionBadge
          v-if="showCurrentVersionBadge"
          label="Current Version"
        />
      </div>

      <div class="d-flex align-center ga-1 flex-wrap">
        <span class="text-body-2 text-textDark font-weight-medium">
          {{ submission.is_exempt ? 'Exempt' : 'Non-exempt' }}
        </span>
        <template v-if="submission.created_by?.name">
          <span class="text-body-2 text-textLight">-</span>
          <span class="text-body-2 text-textLight">{{ submission.created_by.name }}</span>
        </template>
      </div>
    </div>

    <div
      v-if="formYearLabel && !isSelected"
      class="flsa-timeline-item-hover-actions d-flex flex-column align-center justify-center ga-1 py-4 flex-shrink-0"
    >
      <div class="flsa-timeline-item-hover-actions-year d-flex align-center justify-center">
        <span class="text-body-3 text-textDark text-center">
          {{ formYearLabel }}
        </span>
      </div>

      <DpplNewButton
        variant="outlined"
        color="primary"
        height="32"
        class="flsa-timeline-item-hover-actions-view text-button-3 w-100"
        @click.stop="handleClick"
      >
        View
      </DpplNewButton>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import StatusPill from '@/components/shared/StatusPill.vue';
import CurrentVersionBadge from '@/components/shared/CurrentVersionBadge.vue';
import DpplNewButton from '@/components/datapeople/DpplNewButton.vue';
import {
  formatFlsaFormYearLabel,
  formatFlsaSubmissionDate,
  jurisdictionLabel,
  jurisdictionStatusKey,
} from '@/helpers/flsa';

const props = defineProps({
  submission: {
    type: Object,
    required: true,
  },
  isFirst: {
    type: Boolean,
    default: false,
  },
  isLast: {
    type: Boolean,
    default: false,
  },
  showJurisdictionPill: {
    type: Boolean,
    default: false,
  },
  showCurrentVersionBadge: {
    type: Boolean,
    default: false,
  },
  isSelected: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['click']);

const showConnector = computed(() => !(props.isFirst && props.isLast));

const formYearLabel = computed(() => formatFlsaFormYearLabel(props.submission.formYear));

const handleClick = () => {
  if (props.isSelected) return;

  emit('click', props.submission);
};
</script>

<style scoped lang="less">
.flsa-timeline-item-interactive {
  cursor: pointer;

  &:hover {
    background: @bg-canvas;
  }

  &:hover .flsa-timeline-item-hover-actions {
    opacity: 1;
    visibility: visible;
  }
}

.flsa-timeline-item-read-only {
  cursor: default;
  pointer-events: none;
}

.flsa-timeline-item-hover-actions {
  width: 73px;
  opacity: 0;
  visibility: hidden;
}

.flsa-timeline-item-connector {
  width: 9px;
}

.flsa-timeline-item-line {
  width: 2px;
  background-color: @brand-primary;
  left: 50%;
  transform: translateX(-50%);
}

.flsa-timeline-item-dot {
  width: 9px;
  height: 9px;
  background-color: @bg-default;
  border: 2px solid @brand-primary;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1;
}

.flsa-timeline-item-dot-selected {
  background-color: @brand-primary;
  border-color: @brand-primary;
}

.flsa-timeline-item-first .flsa-timeline-item-line {
  height: 50%;
  top: 50%;
}

.flsa-timeline-item:not(.flsa-timeline-item-first):not(.flsa-timeline-item-last) .flsa-timeline-item-line {
  height: 100%;
  top: 0;
}

.flsa-timeline-item-last:not(.flsa-timeline-item-first) .flsa-timeline-item-line {
  height: 50%;
  top: 0;
}

.flsa-timeline-item-hover-actions-view {
  background-color: @bg-default;
}
</style>
